// 覚醒王・大暴れ第5案の本編側の台本 (DEV `?rampage=v5` だけ・採用前)。
// 位置・高さ・向きの駆動、噛みつき4本と怨念の発生、怨念の追尾をここに閉じ込める。
// 他のボスは `atk.choreography === 'awokenRampageV5'` / `hit.spirit` を持たないので、ここを1行も通らない。
// (鶏の宙廻り 'roosterAerialRoam' は別の台本 src/rooster_aerial_roam.ts。下の合図・接触はその技を通さない。
//  settleAltitude だけは技を問わず「台本の技の構え・振り・隙の間は高さを保つ」を共用する。)
// 設計＝docs/handoffs/2026-09-19-awoken-rampage-v5-implementation-ticket.md §5 / §5-10
import { FIXED_DT, PLAYER, PLAYER_DISPLAY_HEIGHT } from './balance'
import type { ActiveHit, AttackDef, Boss, BossString, Player } from './types'
import {
  ROAM, CHOREO_ROAR_SEC, CHOREO_ACTIVE_START_SEC, GLINT_SHOW_SEC, glintTimes, BITE_OFFSET_PX, BITE_STRIKE, BITE_BAND, SPIRIT, WAVE, STAGGER_FALL_PX_PER_TICK,
  ROAM_END, WALL_FOOT_INSET_PX, choreoWallSurfaces, biteWindows, choreoPoseAt, contactTimes, type ChoreoContactKind, defaultFacings, defaultTargets, isWallAnchor, spiritSchedule, wallPoseUsesKickArt, type ChoreoPose,
} from './awoken_rampage_choreo'

const RAMPAGE_V5_ID = 'rampageV5'
// 09-21 ユーザー裁定で配布版へ入れ替えた = 既定で第5案。DEV の `?rampage=old` の時だけ旧の形 (旧・大暴れ／軋む咆哮／旧の幕の並び) に戻る。
//   票 = docs/handoffs/2026-09-21-awoken-rampage-v5-release-ticket.md。戻すならこの1行の向きを逆にする。
const trialOn = !(import.meta.env.DEV && typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('rampage') === 'old')

export function rampageV5Enabled(): boolean { return trialOn }

// 大暴れの怨念の留まり方。09-25 ユーザー裁定「壁際で止まる方が良い」で本番も wall (既定) に切り替えた。
//   wall (既定・本番) = 飛び出すまで生まれた点から動かず、そこから主人公へ直接飛ぶ。届く拍は同じ (飛び出し→着弾は SPIRIT.flightMs のまま)。
//   follow (DEV の比較 `?rampageSpiritHold=follow` だけ) = 旧の動き: 壁で少し留まり → 主人公の周りの席へ漂い → 主人公に付いて行く → 飛び込む。
//   本番ビルドでは常に wall。main.ts の DEV の箱だけが呼ぶ。票 = docs/handoffs/2026-09-24-awoken-wall-hunt-study.md (09-25 追記3・追記4)。
export type RampageSpiritHold = 'follow' | 'wall'
let spiritHold: RampageSpiritHold = 'wall'
export function setRampageSpiritHold(value: string | null | undefined): void {
  spiritHold = import.meta.env.DEV && value === 'follow' ? 'follow' : 'wall'
}
export function rampageSpiritHoldMode(): RampageSpiritHold { return import.meta.env.DEV ? spiritHold : 'wall' }

// 幕3で足していた連。第5案では幕3の主役が大暴れになる (弾き切ればほぼそれだけで幕が終わる) ので、幕2へ前倒しする
//   (09-21 ユーザー裁定「3幕で技を変えたりテンポを変えるのを2幕にうつしたり全体のバランスを調整する」)。
const MOVED_TO_ROOM2 = new Set(['ak-wall-rush', 'ak-bite-bite-rush'])
// 🔴 09-21 取り消し: 噛み→3連噛み (ak-bite-frenzy) を幕1へ回していたが、1連で4回弾けるので幕1が「削らず弾くだけで崩せる」
//   (awoken_posture_goal で5戦中4戦) になり、覚醒王の体幹の決まり「削らないと崩せない」とぶつかった → ユーザー裁定で幕2へ戻した。
//   戻すなら ['ak-bite-frenzy'] を入れる。票 = docs/handoffs/2026-09-21-awoken-rampage-v5-release-ticket.md §6。
const MOVED_TO_ROOM1 = new Set<string>([])

/** DEV の比較: `ak-rampage` の中身を第5案へ差し替え、幕3 (致命2回目以降) だけに出す。幕3の連は幕2へ前倒し。既定は不変。 */
export function rampageV5Strings(boss: Pick<Boss, 'id'>, strings: BossString[], enabled = trialOn): BossString[] {
  if (!enabled || boss.id !== 'crowned-king-awoken') return strings
  return strings.map(s => s.id === 'ak-rampage'
    ? { ...s, unlockAtExecutes: 2, beats: [{ attackId: RAMPAGE_V5_ID }] }
    : MOVED_TO_ROOM2.has(s.id) ? { ...s, unlockAtExecutes: 1 }
    : MOVED_TO_ROOM1.has(s.id) ? { ...s, unlockAtExecutes: 0 } : s)
}

// 初登場の保証 (09-21 技バランス票・案A)。幕2は約9連しかなく、重み抽選のままだと新しい連の半分近くが一度も出ずに幕が終わる。
//   その幕で解禁された連のうち、まだ出ていないものを先に出す (順番は重み抽選)。出し切ったらいつもの抽選。
//   大暴れは幕3の突入で雄叫びから必ず出る (連の外) ので、ここでは初登場扱いしない。
function seenThisAct(boss: Pick<Boss, 'executesTaken' | 'debut'>): string[] {
  return boss.debut?.act === boss.executesTaken ? boss.debut.ids : []
}

/** この幕で解禁された未登場の連。背後応手もこの予約を追い越さない。 */
export function rampageV5PendingDebuts(boss: Pick<Boss, 'id' | 'executesTaken' | 'debut'>, candidates: BossString[], enabled = trialOn): BossString[] {
  if (!enabled || boss.id !== 'crowned-king-awoken') return []
  const seen = seenThisAct(boss)
  return candidates.filter(s => (s.unlockAtExecutes ?? 0) === boss.executesTaken && !seen.includes(s.id)
    && !s.beats.some(b => b.attackId === RAMPAGE_V5_ID))
}

/** 候補をこの幕の未登場の連に絞る。未登場が無ければそのまま。 */
export function rampageV5Debut(boss: Pick<Boss, 'id' | 'executesTaken' | 'debut'>, candidates: BossString[], enabled = trialOn): BossString[] {
  const fresh = rampageV5PendingDebuts(boss, candidates, enabled)
  return fresh.length > 0 ? fresh : candidates
}

/** DEV の比較: 選んだ連をこの幕の「出た」に記録する。既定は何も足さない。 */
export function rampageV5DebutSeen(boss: Pick<Boss, 'id' | 'executesTaken' | 'debut'>, stringId: string, enabled = trialOn): Partial<Boss> {
  if (!enabled || boss.id !== 'crowned-king-awoken') return {}
  return { debut: { act: boss.executesTaken, ids: [...seenThisAct(boss), stringId] } }
}

// 大暴れの噛みを通常パリィした時に自分の体幹へ溜まる量 (09-22 ユーザー裁定・体幹の票 案E)。
//   全ボス共通は 11 (econ)。大暴れは噛みが約1.35秒おきに10回前後あり、通常パリィの回復停止 1.7秒より間が短いので
//   1度も戻らない＝失敗なしでも 93 前後まで溜まり、1回の被弾でほぼ確定で崩れていた。ジャストと同じ 6 にする。
//   票 = docs/handoffs/2026-09-22-player-posture-break-on-parry-ticket.md。
export const RAMPAGE_BITE_NORMAL_PARRY_SELF_GAIN = 6
/** 通常パリィで自分の体幹へ溜まる量。大暴れの噛みだけ軽い。 */
export function normalParrySelfGainFor(attackId: string, base: number): number {
  return attackId === RAMPAGE_V5_ID ? RAMPAGE_BITE_NORMAL_PARRY_SELF_GAIN : base
}

/** DEV の比較: 攻撃の回転を上げる (finalRoomTempoMul) のを幕3からでなく幕2から始める。既定 (undefined) は最終幕だけ。 */
export function rampageV5TempoFromExecutes(bossId: string, enabled = trialOn): number | undefined {
  return enabled && bossId === 'crowned-king-awoken' ? 1 : undefined
}

/** DEV の比較: 幕3の突入の咆哮 (feralRoar) を第5案に差し替える＝雄叫びを1回上げたら暴れ出す (09-20)。既定は不変。 */
export function rampageV5EntryAttack(bossId: string, attackId: string, enabled = trialOn): string {
  return enabled && bossId === 'crowned-king-awoken' && attackId === 'feralRoar' ? RAMPAGE_V5_ID : attackId
}

/** 振付の時計 (秒)。雄叫びの後・最初の構えの頭を 0 とする (雄叫びの間は負)。 */
export function choreoClockSec(boss: Pick<Boss, 'state' | 'stateElapsed'>, atk: Pick<AttackDef, 'chargeMs'>): number {
  const raw = boss.state === 'telegraph' ? boss.stateElapsed : atk.chargeMs / 1000 + boss.stateElapsed
  return raw - CHOREO_ROAR_SEC
}

/** 前tick→今tickの間に光る合図が始まったら、その色 (音を鳴らすため)。 */
export function glintStarted(before: Boss, after: Boss): 'red' | 'white' | null {
  const atk = after.currentAttack
  if (atk?.choreography !== 'awokenRampageV5' || !after.choreo || before.currentAttack !== atk) return null
  if (after.state !== 'telegraph' && after.state !== 'active') return null
  const t0 = before.state === 'telegraph' || before.state === 'active' ? choreoClockSec(before, atk) : -Infinity
  const t1 = choreoClockSec(after, atk)
  return glintTimes().find(g => g.at > t0 && g.at <= t1)?.color ?? null
}

/** 今光っている合図 (描画用)。進み 0→1 と色。 */
export function activeGlint(boss: Boss): { color: 'red' | 'white'; progress: number } | null {
  const atk = boss.currentAttack
  if (atk?.choreography !== 'awokenRampageV5' || (boss.state !== 'telegraph' && boss.state !== 'active')) return null
  const t = choreoClockSec(boss, atk)
  const g = glintTimes().find(x => t >= x.at && t < x.at + GLINT_SHOW_SEC)
  return g ? { color: g.color, progress: (t - g.at) / GLINT_SHOW_SEC } : null
}

// 体が壁・床に触れた瞬間 (09-21 総評 B-1/B-2/B-3/B-4)。game.ts が揺れ・粒・既存の音へ変える。判定には関わらない。
//   roar = 雄叫びで頭を上げた瞬間／collapse = 暴れ切って倒れ込んだ瞬間。x,y は粒を出す点、dir は粒が飛ぶ向き (壁なら部屋側)。
export type RampageContact = { kind: ChoreoContactKind | 'roar' | 'collapse'; x: number; y: number; dir: number }
const ROAR_PEAK_SEC = 0.3 - CHOREO_ROAR_SEC   // 時間割で `roar_up` の絵に替わる瞬間 (boss_pose_timeline.ts)
const CONTACTS = contactTimes()
export function rampageContacts(before: Boss, after: Boss): RampageContact[] {
  const atk = after.currentAttack
  if (atk?.choreography !== 'awokenRampageV5' || before.currentAttack !== atk) return []
  const floor = (b: Boss): { x: number; y: number; dir: number } =>
    ({ x: b.x, y: b.y, dir: b.facing === 'left' ? -1 : 1 })
  if (after.state === 'recovery') return before.state === 'active' ? [{ kind: 'collapse', ...floor(after) }] : []
  if (after.state !== 'telegraph' && after.state !== 'active') return []
  const t0 = before.state === 'telegraph' || before.state === 'active' ? choreoClockSec(before, atk) : -Infinity
  const t1 = choreoClockSec(after, atk)
  const out: RampageContact[] = []
  if (ROAR_PEAK_SEC > t0 && ROAR_PEAK_SEC <= t1) out.push({ kind: 'roar', ...floor(after) })
  for (const c of CONTACTS) {
    if (!(c.at > t0 && c.at <= t1) || c.at >= ROAM_END + 1e-9) continue
    if (c.kind === 'wall_kick' || c.kind === 'wall_land') {
      // 蹴る瞬間は前tickの位置 (まだ壁にいる)、着く瞬間は今tickの位置。壁面は後ろ足の先 = 体の中心から 106px 外。
      const b = c.kind === 'wall_kick' ? before : after
      const w = choreoWallSurfaces(), side = b.x > (w.left + w.right) / 2 ? 1 : -1
      out.push({ kind: c.kind, x: b.x + side * WALL_FOOT_INSET_PX, y: b.y - (b.altitudePx ?? 0) - 70, dir: -side })
    } else out.push({ kind: c.kind, ...floor(c.kind === 'ground_kick' ? before : after) })
  }
  return out
}

// 顔の合図 (パリィの「ここ！」) を噛みつきごとに付け替える (09-21 総評: 技全体で1回だと移動中も光りっぱなしになる)。
//   converge = 構えの頭から口が閉じる瞬間まで光が収束／flash = 口が閉じてからの閃光／rest = 移動・壁の間は落とす。
const FACE_FLASH_SEC = .25
type FaceCueWindow = { cueFrom: number; strike: number }
const FACE_CUE_WINDOWS: FaceCueWindow[] = (() => {
  const bites = biteWindows()
  let start = 0
  const heads = new Map<number, number>()
  ROAM.forEach((leg, pass) => { heads.set(pass, start); start += leg.warning + leg.travel })
  return bites.map(w => ({ cueFrom: heads.get(w.pass)!, strike: w.from }))
})()

export type RampageFaceCue =
  | { kind: 'roar'; p: number }
  | { kind: 'converge'; p: number }
  | { kind: 'flash'; p: number }
  | { kind: 'rest' }

/** 大暴れ中の顔の合図。台本の技でなければ null (従来の合図のまま)。 */
export function rampageFaceCue(boss: Boss): RampageFaceCue | null {
  const atk = boss.currentAttack
  if (atk?.choreography !== 'awokenRampageV5' || (boss.state !== 'telegraph' && boss.state !== 'active')) return null
  const t = choreoClockSec(boss, atk)
  if (t < 0) return { kind: 'roar', p: Math.min(1, 1 + t / CHOREO_ROAR_SEC) }
  for (const [i, w] of FACE_CUE_WINDOWS.entries()) {
    if (t >= w.cueFrom && t < w.strike) return { kind: 'converge', p: (t - w.cueFrom) / (w.strike - w.cueFrom) }
    // 閃光は次の噛みの構えが始まったら打ち切る (続けざまの噛みで次の収束を隠さない)。
    const flashEnd = Math.min(w.strike + FACE_FLASH_SEC, FACE_CUE_WINDOWS[i + 1]?.cueFrom ?? Infinity)
    if (t >= w.strike && t < flashEnd) return { kind: 'flash', p: (t - w.strike) / FACE_FLASH_SEC }
  }
  return { kind: 'rest' }
}

function poseOf(boss: Boss, t: number): ChoreoPose {
  const c = boss.choreo!
  return choreoPoseAt(t, c.startX, c.targets, c.facings)
}

/**
 * telegraph / active の毎tick、踏み込み処理の代わりに呼ぶ。
 * 構えの頭で台本を作り、噛みつきの構えに入った瞬間だけ着地点と向きを固定する (途中で追い直さない)。
 */
export function advanceChoreo(boss: Boss, player: Player, atk: AttackDef, bounds: { leftWall: number; rightWall: number }): Boss {
  let next = boss
  if (!next.choreo) {
    // 右から入る型が基準。ボスが中央より左にいれば鏡写し。出発点は今いる場所。
    const mirror = next.x < 480
    next = { ...next, choreo: { startX: next.x, mirror, targets: defaultTargets(mirror),
      facings: defaultFacings(mirror, next.x), emitted: 0 } }
  }
  const t = choreoClockSec(next, atk)
  // 雄叫びの間はその場で主人公を向いたまま (台本はまだ始まらない)。
  if (t < 0) return { ...next, altitudePx: 0 }
  const prevPose = poseOf(next, Math.max(0, t - FIXED_DT))
  let pose = poseOf(next, t)
  if (pose.kind !== 'run' && pose.pass !== prevPose.pass) {
    const dir = player.x < pose.from ? -1 : 1
    const c = next.choreo!
    const targets = [...c.targets]
    targets[pose.pass] = Math.max(bounds.leftWall, Math.min(bounds.rightWall, player.x - dir * BITE_OFFSET_PX))
    const facings = [...c.facings]; facings[pose.pass] = dir
    next = { ...next, choreo: { ...c, targets, facings } }
    pose = poseOf(next, t)
  }
  const x = Math.max(bounds.leftWall, Math.min(bounds.rightWall, pose.x))
  // 壁にしがみついて蹴り出しを待つ間は、壁の側を向く (腹を壁に付ける)。噛みつきの構えは主人公を向いたまま。
  let facing = pose.facing
  if (pose.warning && pose.kind === 'run' && isWallAnchor(pose.from, ROAM[pose.pass - 1]?.y ?? 0, next.choreo!.mirror)) {
    // 壁の絵を使う案は、原画が「壁に四肢を付けて頭は部屋側」なので向きが逆になる。
    facing = (pose.from > 480 ? 1 : -1) * (wallPoseUsesKickArt() ? -1 : 1)
  }
  return { ...next, x, altitudePx: Math.max(0, pose.height), facing: facing < 0 ? 'left' : 'right' }
}

const SPIRIT_BEATS = spiritSchedule()

/** 前tick→今tickで発生させる怨念の数 (壁を蹴った瞬間ごと)。 */
export function spiritsDue(before: Boss, after: Boss): number {
  const b = before.choreo?.emitted ?? 0
  const a = after.choreo?.emitted ?? 0
  return Math.max(0, a - b)
}

/** 発生回数の帳尻を今の時計に合わせる (advanceChoreo の後に呼ぶ)。 */
export function tallySpirits(boss: Boss, atk: AttackDef): Boss {
  if (!boss.choreo) return boss
  const t = choreoClockSec(boss, atk)
  const due = SPIRIT_BEATS.filter(b => b.spawnAt <= t + 1e-9).length
  return due > boss.choreo.emitted ? { ...boss, choreo: { ...boss.choreo, emitted: due } } : boss
}

/** 台本の技でない状態へ移ったら高さと台本を片付ける。崩れた時だけは床まで落とす。 */
export function settleAltitude(boss: Boss): Boss {
  const inChoreo = !!boss.currentAttack?.choreography && (boss.state === 'telegraph' || boss.state === 'active' || boss.state === 'recovery')
  if (inChoreo) return boss
  const alt = boss.altitudePx ?? 0
  const fallen = boss.state === 'staggered' ? Math.max(0, alt - STAGGER_FALL_PX_PER_TICK) : 0
  if (alt === fallen && !boss.choreo) return boss
  const { choreo: _c, ...rest } = boss
  return { ...rest, altitudePx: fallen }
}

let nextChoreoHitId = 900000

/** active 突入時に4本の噛みつきをまとめて出す。窓は active の時計 (boss.stateElapsed) で開閉する。 */
export function spawnChoreoBites(atk: AttackDef): ActiveHit[] {
  const chargeSec = CHOREO_ACTIVE_START_SEC
  return biteWindows().map(w => ({
    id: nextChoreoHitId++, attackId: atk.id, kind: 'melee' as const, follow: 'boss' as const,
    range: BITE_STRIKE.localToPx, strike: { ...BITE_STRIKE }, band: { ...BITE_BAND }, mouth: true as const,
    xStart: 0, xEnd: 0, elapsedMs: 0, telegraphMs: 0,
    contactDelayMs: Math.max(0, (w.from - chargeSec) * 1000),
    activeMs: (w.to - chargeSec) * 1000,
    damage: atk.damage, postureDamage: atk.postureDamage, parryable: true,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false,
    parryIntensity: 1,
  }))
}

/** 台本の index 番目の飛び道具を出す。怨念なら1個、床技の衝撃波なら左右へ2本。 */
export function spawnCue(boss: Boss, player: Player, index: number): ActiveHit[] {
  const beat = SPIRIT_BEATS[Math.min(index, SPIRIT_BEATS.length - 1)]!
  return beat.wave ? [-1, 1].map(dir => spawnWave(boss, dir)) : [spawnSpirit(boss, player, index)]
}

/** 床技の衝撃波1本: 着地点から床すれすれを dir の向きへ這う。追尾しない・弾けない (回避で抜ける)。仮値。 */
export function spawnWave(boss: Boss, dir: number): ActiveHit {
  const x = boss.x + dir * WAVE.startOffsetPx, y = boss.y - WAVE.heightPx / 2
  return {
    id: nextChoreoHitId++, attackId: 'rampageV5Wave', kind: 'melee', follow: 'travel',
    x, y, vx: dir * WAVE.speedPxPerSec, vy: 0, widthPx: WAVE.widthPx, launchDelayMs: 0,
    xStart: x - WAVE.widthPx / 2, xEnd: x + WAVE.widthPx / 2,
    elapsedMs: 0, telegraphMs: 0, activeMs: 0,
    damage: 16, postureDamage: 0, parryable: false,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false, parryIntensity: 0.6,
    spirit: { turnRadPerSec: 0, homingMs: 0, rangePx: WAVE.rangePx, traveledPx: 0, wave: true },
  }
}

/** 怨念1個 (index = 台本の何個目か)。発生点に留まる間は当たらない (launchDelayMs)。
 *  留まる長さ = 届く拍 − 一定の飛行時間 (SPIRIT.flightMs)。どこに立っていても「飛び出し → 着弾」が同じ長さ。赤＝弾けない。 */
export function spawnSpirit(boss: Boss, player: Player, index = 0): ActiveHit {
  const beat = SPIRIT_BEATS[Math.min(index, SPIRIT_BEATS.length - 1)]!
  // 同じ瞬間に複数出る時は重ねない (同じ点だと1個に見える・09-21 実画面)。2個目は上、3個目は部屋側の斜め上。届く拍は変わらない。
  let group = 0
  while (group < index && SPIRIT_BEATS[index - group - 1]?.spawnAt === beat.spawnAt && !SPIRIT_BEATS[index - group - 1]?.wave) group++
  return spawnTimedSpirit(boss, player, (beat.arriveAt - beat.spawnAt) * 1000, group, beat.red, rampageSpiritHoldMode() === 'wall')
}

/** 怨念1個の中身。spawnSpirit と、DEV の壁跳ね新版の帰りに残す怨念 (awoken_wall_hunt_game.ts) が共有する。
 *  arriveMs = 生まれてから主人公に触れるまで／group = 同じ瞬間に生まれた中の何個目か (置き場所だけずらす)／red = 弾けない／
 *  hold = 飛び出すまで生まれた点から動かない (大暴れの既定・壁跳ね新版の壁の怨念。false = 旧の席へ漂う動き・hoverSpirit 参照)。 */
export function spawnTimedSpirit(boss: Boss, player: Player, arriveMs: number, group = 0, red = false, hold = false): ActiveHit {
  // 体の中心ではなく部屋側 (頭の先) に生む: 体の上だと王の絵に隠れる (09-21)。壁にいる時は部屋の中央の側。
  const roomward = boss.x > 480 ? -1 : 1
  const x = boss.x + roomward * (SPIRIT.spawnRoomwardPx + (group >= 2 ? SPIRIT.pairUpPx : 0))
  const y = boss.y - SPIRIT.spawnUpPx - (boss.altitudePx ?? 0) - (group === 1 ? SPIRIT.pairUpPx : group >= 2 ? SPIRIT.pairUpPx / 2 : 0)
  const hangMs = Math.max(SPIRIT.minHangMs, arriveMs - SPIRIT.flightMs)
  const angle = Math.atan2(player.y - 50 - y, player.x - x)
  return {
    id: nextChoreoHitId++, attackId: 'rampageV5Spirit', kind: 'melee', follow: 'travel',
    // 留まっている間の向き (描画用)。飛び出した tick から steerSpirit が拍に合わせて速さを決め直す。
    x, y, vx: Math.cos(angle) * SPIRIT.speedPxPerSec, vy: Math.sin(angle) * SPIRIT.speedPxPerSec,
    widthPx: SPIRIT.widthPx, launchDelayMs: hangMs,
    xStart: x - SPIRIT.widthPx / 2, xEnd: x + SPIRIT.widthPx / 2,
    elapsedMs: 0, telegraphMs: 0, activeMs: 0,
    // 被ダメ・体幹は仮値 (駅4で詰める)。
    damage: 14, postureDamage: 18, parryable: !red,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false, parryIntensity: 0.6,
    spirit: { turnRadPerSec: SPIRIT.turnRadPerSec, homingMs: SPIRIT.homingMs, rangePx: SPIRIT.rangePx, traveledPx: 0, arriveMs,
      seenPlayerX: player.x, hover: { fromX: x, fromY: y, slot: group, side: 0, ...(hold ? { hold: true } : {}) } },
  }
}

/** 主人公の周りの席 (slot 番目・side の側)。席までの距離 = 飛び込む距離 + 触れる点までの距離 なので、どの席からでも
 *  「触れるまでに進む距離」が同じ = 同じ速さ・同じ時間で届く。壁の外へ出る時は x を内側へ寄せ、その分だけ高くして距離を保つ。 */
export function spiritSlot(player: Player, slot: number, side: number, r: number): { x: number; y: number } {
  const deg = SPIRIT.slotDeg[Math.min(slot, SPIRIT.slotDeg.length - 1)]!
  const th = deg * Math.PI / 180
  const touch = Math.min((r + PLAYER.width / 2) / Math.cos(th), (PLAYER_DISPLAY_HEIGHT - 50 + r) / Math.sin(th))
  const dist = SPIRIT.diveGapPx + touch
  const w = choreoWallSurfaces()
  const x = Math.max(w.left + r, Math.min(w.right - r, player.x + side * dist * Math.cos(th)))
  const dx = x - player.x
  return { x, y: player.y - 50 - Math.sqrt(Math.max(0, dist * dist - dx * dx)) }
}

/** 飛び出す前の怨念 (hits.ts が留まりの間に呼ぶ)。当たりは無い。
 *  hold (大暴れの既定・09-25 裁定) の時は生まれた点から動かない。hold が無い時 (DEV の `?rampageSpiritHold=follow`) は旧の動き =
 *  壁で少し留まり → 席へ漂い → 席に着いたら主人公に付いて行く。hold の時も頭の向き (描画用の速度) と前 tick の主人公の x だけ毎 tick 更新する
 *  (前者は描画だけ、後者は飛び出した tick に steerSpirit が留まりの間の主人公の移動をまとめて足さないため)。 */
export function hoverSpirit(hit: ActiveHit, player: Player): ActiveHit {
  const s = hit.spirit, h = s?.hover
  if (!s || !h || s.arriveMs === undefined || hit.reflected) return hit
  const launch = hit.launchDelayMs ?? 0
  const hold = !!h.hold
  let side = h.side
  if (!hold && hit.elapsedMs >= SPIRIT.wallHangMs && side === 0) {
    // 生まれた壁の側の席に着く。その側に余地が無い (主人公が壁際) 時は反対側。
    const wallSide = h.fromX >= player.x ? 1 : -1
    const w = choreoWallSurfaces()
    const room = wallSide > 0 ? w.right - player.x : player.x - w.left
    side = room >= SPIRIT.sideRoomPx ? wallSide : -wallSide
  }
  let x = h.fromX, y = h.fromY
  if (side !== 0) {
    const span = Math.max(1, launch - SPIRIT.settleMs - SPIRIT.wallHangMs)
    const u = Math.max(0, Math.min(1, (hit.elapsedMs - SPIRIT.wallHangMs) / span))
    const e = u * u * (3 - 2 * u)
    const seat = spiritSlot(player, h.slot, side, (hit.widthPx ?? 0) / 2)
    x = h.fromX + (seat.x - h.fromX) * e; y = h.fromY + (seat.y - h.fromY) * e
  }
  // 頭はいつも主人公を向く (描画は速度の向きを読む)。
  const a = Math.atan2(player.y - 50 - y, player.x - x)
  const w = (hit.widthPx ?? 0) / 2
  return { ...hit, x, y, xStart: x - w, xEnd: x + w, vx: Math.cos(a) * SPIRIT.speedPxPerSec, vy: Math.sin(a) * SPIRIT.speedPxPerSec,
    spirit: { ...s, seenPlayerX: player.x, hover: { ...h, side } } }
}

/** 拍どおりに届く怨念の速度: 今の位置から主人公の胴へ向かう線の上で、「体に触れる点」までを残り時間で割る。
 *  触れる点 = 横が重なり (半径＋主人公の半幅)、かつ縦が主人公の背丈に入る所。どちらも満たす最初の点。 */
function timedSpiritVelocity(hit: ActiveHit, player: Player, remainMs: number): { vx: number; vy: number } {
  const tx = player.x - hit.x!, ty = player.y - 50 - hit.y!
  const r = (hit.widthPx ?? 0) / 2
  const reachX = r + PLAYER.width / 2
  const sx = Math.abs(tx) <= reachX ? 0 : 1 - reachX / Math.abs(tx)
  // 縦: 玉の下端が主人公の頭 (足元 − 背丈) に届く所。胴は頭より 背丈−50 下。
  const headGap = (player.y - PLAYER_DISPLAY_HEIGHT) - (hit.y! + r)
  const sy = headGap <= 0 || ty <= 0 ? 0 : Math.min(1, headGap / ty)
  const s = Math.max(sx, sy)
  const sec = Math.max(remainMs, FIXED_DT * 1000) / 1000
  return { vx: tx * s / sec, vy: ty * s / sec }
}

/** 怨念の舵取り。敵の怨念は主人公の胴を、返弾はボスの胴 (高さ込み) を追う。移動前に呼ぶ。 */
export function steerSpirit(hit: ActiveHit, player: Player, boss: Boss, dtMs: number): ActiveHit {
  const s = hit.spirit
  if (!s || hit.x === undefined || hit.y === undefined || hit.vx === undefined) return hit
  const vy = hit.vy ?? 0
  if (hit.reflected) {
    const ty = boss.y - (boss.altitudePx ?? 0) - 48
    const a = Math.atan2(ty - hit.y, boss.x - hit.x)
    return { ...hit, vx: Math.cos(a) * SPIRIT.reflectSpeedPxPerSec, vy: Math.sin(a) * SPIRIT.reflectSpeedPxPerSec }
  }
  if (s.arriveMs !== undefined) {
    // hit.elapsedMs はこの tick の終わりの時刻。これから動くのは [elapsedMs − dt, elapsedMs]。
    const remainMs = s.arriveMs - (hit.elapsedMs - dtMs)
    if (remainMs > SPIRIT.lockMs) {
      // 主人公が動いた分だけ玉も動かす = 主人公から見た飛び込みは、歩いても回避しても同じ速さ・同じ長さ。
      //   walkFollowOnly (壁跳ねの帰りの怨念・10-07) は歩きの速さまで＝回避の間に玉が一緒に滑らない (拍に間に合うよう向きと速さは毎 tick 合わせ直す)。
      const moved0 = player.x - (s.seenPlayerX ?? player.x)
      const cap0 = PLAYER.moveSpeed * dtMs / 1000
      const follow = s.walkFollowOnly ? Math.max(-cap0, Math.min(cap0, moved0)) : moved0
      const moved = { ...hit, x: hit.x + follow, spirit: { ...s, seenPlayerX: player.x } }
      const v = timedSpiritVelocity(moved, player, remainMs)
      // もう触れる位置にいる時は止めずに今の速度のまま進む。
      return Math.hypot(v.vx, v.vy) < 1e-6 ? moved : { ...moved, ...v }
    }
    // 着弾の直前は向きと速さを固定。歩きの速さまでは付いて行く (歩いて拍をずらせない) が、回避の速さには付いて行かない。
    if (remainMs > 0) {
      const cap = PLAYER.moveSpeed * dtMs / 1000
      const follow = Math.max(-cap, Math.min(cap, player.x - (s.seenPlayerX ?? player.x)))
      return { ...hit, x: hit.x + follow, spirit: { ...s, seenPlayerX: player.x } }
    }
    // 拍を過ぎたら最低の速さまで上げて抜けていく (遅い玉が体の上に居座らないように)。
    const speed = Math.hypot(hit.vx, vy)
    if (remainMs <= 0 && speed > 1e-6 && speed < SPIRIT.speedPxPerSec) {
      const k = SPIRIT.speedPxPerSec / speed
      return { ...hit, vx: hit.vx * k, vy: vy * k }
    }
    return hit
  }
  const flying = hit.elapsedMs - (hit.launchDelayMs ?? 0)
  if (flying < 0 || flying > s.homingMs) return hit
  const speed = Math.hypot(hit.vx, vy)
  const angle = Math.atan2(vy, hit.vx)
  const desired = Math.atan2(player.y - 50 - hit.y, player.x - hit.x)
  const diff = Math.atan2(Math.sin(desired - angle), Math.cos(desired - angle))
  const limit = s.turnRadPerSec * dtMs / 1000
  const a = angle + Math.max(-limit, Math.min(limit, diff))
  return { ...hit, vx: Math.cos(a) * speed, vy: Math.sin(a) * speed }
}

/** 移動後に進んだ距離を積む (敵の怨念だけ。返弾は別の寿命)。 */
export function recordSpiritTravel(hit: ActiveHit, dx: number, dy: number): ActiveHit {
  if (!hit.spirit || hit.reflected) return hit
  return { ...hit, spirit: { ...hit.spirit, traveledPx: hit.spirit.traveledPx + Math.hypot(dx, dy) } }
}

/** 怨念の寿命: 敵の怨念は射程、返弾は返してから3秒。 */
export function spiritExpired(hit: ActiveHit): boolean {
  const s = hit.spirit
  if (!s) return false
  if (hit.reflected) return hit.elapsedMs - (s.reflectedAtMs ?? hit.elapsedMs) >= SPIRIT.reflectLifeMs
  return s.traveledPx >= s.rangePx
}

/** 怨念は円として縦も見る (頭上を飛ぶ怨念は当たらない)。feetY から height までの帯と重なるか。 */
export function spiritOverlapsHeight(hit: ActiveHit, feetY: number, height: number): boolean {
  if (!hit.spirit || hit.y === undefined) return true
  // 床の衝撃波は横幅 (widthPx) と高さ (WAVE.heightPx) が別。横幅の半分で縦を見ると、床から 7px 浮いた 20px の帯になっていた (09-21 Codex 指摘)。
  const r = hit.spirit.wave ? WAVE.heightPx / 2 : (hit.widthPx ?? 0) / 2
  return hit.y + r >= feetY - height && hit.y - r <= feetY
}

/** 高さを見る近接ヒット (噛みつき): 口の帯 [alt+from, alt+to] と主人公の 0..height の重なり。 */
export function bandOverlapsPlayer(hit: ActiveHit, boss: Boss, playerHeight: number): boolean {
  if (!hit.band) return true
  const alt = boss.altitudePx ?? 0
  return alt + hit.band.fromPx <= playerHeight && alt + hit.band.toPx >= 0
}

